import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Maximize, X } from 'lucide-react';
import { useContainerDimensions } from '../hooks/useContainerDimensions';
import { AnnotationLayer, type AnnotationTask, type NewTaskClickData } from './AnnotationLayer';
import type { LineType } from '../lib/lineMath';

/** Data produced when the user clicks/draws on the image */
export interface ImageNewTaskData {
  /** Natural image pixel coordinates */
  coord: { left: number; top: number };
  /** Percentage coordinates (0–100) for positioning UI */
  percentX: number;
  percentY: number;
  /** Raw pixel coords for arrow storage */
  coords?: [number, number][] | null;
  /** Line type if drawn */
  taskType?: LineType;
  /** Container dimensions at time of drawing (for line preview) */
  containerWidth?: number;
  containerHeight?: number;
}

interface ImageViewerProps {
  resource: {
    id: string;
    signedUrl?: string;
    thumbnailUrl?: string;
    [k: string]: unknown;
  };
  tasks: AnnotationTask[];
  selectedTask?: AnnotationTask | null;
  updateSelectedTask: (task: AnnotationTask | null) => void;
  messagesTabOpen: boolean;
  userRole: string | null;
  onCreateTask?: (data: ImageNewTaskData) => void;
}

export function ImageViewer({
  resource,
  tasks,
  selectedTask,
  updateSelectedTask,
  messagesTabOpen,
  userRole,
  onCreateTask,
}: ImageViewerProps) {
  const { ref: containerRef, dimensions } = useContainerDimensions();
  const { ref: fullscreenRef, dimensions: fullscreenDims } = useContainerDimensions();
  const imgRef = useRef<HTMLImageElement>(null);
  const [loading, setLoading] = useState(true);
  const [imageErrored, setImageErrored] = useState(false);
  const [fullScreen, setFullScreen] = useState(false);
  const [thumbLoaded, setThumbLoaded] = useState(false);

  const isViewer = userRole === 'viewer' || userRole === 'guest';
  const tasksVisible = !messagesTabOpen;

  // Reset loading on resource change
  useEffect(() => {
    setLoading(true);
    setThumbLoaded(false);
    setImageErrored(false);
  }, [resource.id]);

  // Exit fullscreen on scroll
  useEffect(() => {
    if (!fullScreen) return;
    const onScroll = () => {
      if (window.scrollY > 0) setFullScreen(false);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [fullScreen]);

  const handleImageLoaded = useCallback(() => {
    setLoading(false);
  }, []);

  // Transform stored natural-pixel coords → display percentages for task dots
  const coordTransform = useCallback(
    (task: AnnotationTask) => {
      const img = imgRef.current;
      if (!img || !img.naturalWidth) return { x: 0, y: 0 };
      const rawX = task.coord_x ?? task.coordX ?? 0;
      const rawY = task.coord_y ?? task.coordY ?? 0;
      return {
        x: (Number(rawX) / img.naturalWidth) * 100,
        y: (Number(rawY) / img.naturalHeight) * 100,
      };
    },
    [],
  );

  // Wrap AnnotationLayer's percentage-based onCreateTask to produce ImageNewTaskData
  const handleAnnotationCreate = useCallback(
    (data: NewTaskClickData) => {
      const img = imgRef.current;
      if (!img || !onCreateTask) return;
      onCreateTask({
        coord: {
          left: Math.round((data.coordX / 100) * img.naturalWidth),
          top: Math.round((data.coordY / 100) * img.naturalHeight),
        },
        percentX: data.coordX,
        percentY: data.coordY,
        coords: data.pixelCoords ?? null,
        taskType: data.taskType,
        containerWidth: data.containerWidth,
        containerHeight: data.containerHeight,
      });
    },
    [onCreateTask],
  );

  // Wrap updateSelectedTask to convert natural-pixel coords to percentages
  // so TaskPopover (rendered in WorkSection) can position itself correctly
  const handleSelectTask = useCallback(
    (task: AnnotationTask | null) => {
      if (task) {
        const { x, y } = coordTransform(task);
        updateSelectedTask({ ...task, coordX: x, coordY: y });
      } else {
        updateSelectedTask(task);
      }
    },
    [updateSelectedTask, coordTransform],
  );

  const showAnnotations = tasksVisible && !loading && dimensions.width > 0;

  return (
    <>
      {/* Fullscreen overlay */}
      {fullScreen && (
        <div className="fixed inset-0 z-[105] flex items-center justify-center bg-black/90">
          {!imageErrored && resource.signedUrl && (
            <div
              ref={fullscreenRef as React.RefObject<HTMLDivElement>}
              className="relative max-h-[800px] max-w-[1400px]"
            >
              <img
                src={resource.signedUrl}
                alt="preview"
                className="max-h-[80vh] max-w-[1400px] object-scale-down xl:max-w-[1000px] lg:max-w-[700px] md:max-w-[400px] sm:max-w-[200px]"
              />
              {showAnnotations && fullscreenDims.width > 0 && (
                <AnnotationLayer
                  containerRef={fullscreenRef}
                  dimensions={fullscreenDims}
                  tasks={tasks}
                  selectedTask={selectedTask}
                  onSelectTask={handleSelectTask}
                  onCreateTask={onCreateTask ? handleAnnotationCreate : undefined}
                  canCreate={!isViewer && !!onCreateTask}
                  mediaType="image"
                  tasksVisible={tasksVisible}
                  coordTransform={coordTransform}
                />
              )}
            </div>
          )}
          <button
            type="button"
            onClick={() => setFullScreen(false)}
            className="fixed top-8 right-8 flex size-8 cursor-pointer items-center justify-center rounded hover:scale-105 transition-transform"
          >
            <X className="size-8 text-white" />
          </button>
        </div>
      )}

      {/* Regular view */}
      <div
        ref={containerRef as React.RefObject<HTMLDivElement>}
        className="relative mx-auto flex h-full max-h-[calc(35rem-2px)] max-w-full items-center justify-center overflow-hidden"
      >
        {!imageErrored && resource.signedUrl && (
          <>
            {/* Thumbnail (shown while full image loads) */}
            {resource.thumbnailUrl && !thumbLoaded && (
              <img
                src={resource.thumbnailUrl}
                alt="thumb"
                className={loading ? 'block max-h-full max-w-full object-contain' : 'hidden'}
              />
            )}

            {/* Full image */}
            <img
              ref={imgRef}
              src={resource.signedUrl}
              alt="preview"
              className={`max-h-full max-w-full object-contain ${loading ? 'hidden' : 'block'}`}
              onLoad={handleImageLoaded}
              onError={() => setImageErrored(true)}
            />
          </>
        )}

        {/* Annotations (unified via AnnotationLayer) */}
        {showAnnotations && (
          <AnnotationLayer
            containerRef={containerRef}
            dimensions={dimensions}
            tasks={tasks}
            selectedTask={selectedTask}
            onSelectTask={handleSelectTask}
            onCreateTask={onCreateTask ? handleAnnotationCreate : undefined}
            canCreate={!isViewer && !!onCreateTask}
            mediaType="image"
            tasksVisible={tasksVisible}
            coordTransform={coordTransform}
          />
        )}

        {/* Fullscreen toggle */}
        <button
          type="button"
          onClick={() => setFullScreen((v) => !v)}
          className="absolute bottom-2.5 right-2.5 z-[3] flex size-6 cursor-pointer items-center justify-center rounded-sm bg-black/60 hover:scale-105 transition-transform"
        >
          <Maximize className="size-4 text-white" />
        </button>
      </div>
    </>
  );
}
